// src/components/ProductSlider.js
import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { FiPlus, FiMinus, FiTrash2 } from 'react-icons/fi';
import { getImageUrl } from '../utils/getImageUrl';
import { AuthContext } from '../context/AuthContext';
import { useCart } from '../hooks/useCart';
import { useProductDiscountContext } from '../context/ProductDiscountContext';
import { useCustomerContext } from '../context/CustomerContext';
import apiClient from '../services/api';

const FALLBACK_IMAGE = '/static/images/no-image.png';

/**
 * ProductSlider - Horizontal slider of product cards for a banner of type 'products'.
 * Sits in the same catalog slots as BannerSlider and shares its look, but the cards
 * come from the banner's product selection (hand-picked or badge-driven).
 */
const ProductSlider = ({
  banner,
  isMobile = false,
  autoPlay = true,
  interval = 5000,
  className = ''
}) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  const { selectedCustomer } = useCustomerContext();
  const {
    items: cartItems,
    addItem: handleAddToCart,
    updateItem: handleUpdateItem,
    removeItem: handleRemoveFromCart
  } = useCart();
  const { fetchDiscountsForProducts, getDiscountBadgeInfo } = useProductDiscountContext();

  const [startIndex, setStartIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [busyProductId, setBusyProductId] = useState(null);
  const [customerPrices, setCustomerPrices] = useState({});
  const [imageError, setImageError] = useState({});

  const products = useMemo(() => banner?.slider_products || [], [banner]);
  const productIds = useMemo(() => products.map(p => p.id).filter(id => id != null), [products]);
  const productIdsString = useMemo(() => productIds.join(','), [productIds]);

  const perView = Math.max(
    1,
    parseInt(isMobile ? (banner?.products_per_view_mobile || 2) : (banner?.products_per_view || 5), 10) || 1
  );
  const rows = Math.max(1, parseInt(banner?.product_rows || 1, 10) || 1);

  // Products fill column by column, so one slide step moves a whole column
  const columns = useMemo(() => {
    const grouped = [];
    for (let i = 0; i < products.length; i += rows) {
      grouped.push(products.slice(i, i + rows));
    }
    return grouped;
  }, [products, rows]);

  const maxIndex = Math.max(0, columns.length - perView);

  useEffect(() => {
    if (productIds.length > 0 && isAuthenticated) {
      fetchDiscountsForProducts(productIds, selectedCustomer?.id || null);
    }
  }, [productIdsString, selectedCustomer?.id, fetchDiscountsForProducts, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Customer-specific prices, same source the catalog grid uses
  useEffect(() => {
    const fetchCustomerPrices = async () => {
      if (!selectedCustomer?.id || productIds.length === 0) {
        setCustomerPrices({});
        return;
      }
      try {
        const response = await apiClient.post('/orders/get_customer_prices/', {
          product_ids: productIds,
          customer_id: selectedCustomer.id
        });
        setCustomerPrices(response.data || {});
      } catch (error) {
        console.error('[ProductSlider] Error fetching customer prices:', error);
        setCustomerPrices({});
      }
    };

    fetchCustomerPrices();
  }, [selectedCustomer?.id, productIdsString]); // eslint-disable-line react-hooks/exhaustive-deps

  const getEffectivePrice = useCallback((product) => {
    if (selectedCustomer?.id && customerPrices[product.id?.toString()]) {
      return parseFloat(customerPrices[product.id.toString()].price);
    }
    return parseFloat(product.price || 0);
  }, [selectedCustomer?.id, customerPrices]);

  const next = useCallback(() => {
    setStartIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  const prev = useCallback(() => {
    setStartIndex(prev => (prev <= 0 ? maxIndex : prev - 1));
  }, [maxIndex]);

  useEffect(() => {
    if (!autoPlay || maxIndex === 0 || isPaused) return;
    const timer = setInterval(next, interval);
    return () => clearInterval(timer);
  }, [autoPlay, interval, next, maxIndex, isPaused]);

  useEffect(() => {
    setStartIndex(prev => Math.min(prev, maxIndex));
  }, [maxIndex]);

  const getUnitForProduct = useCallback((product) => {
    const normalizedExtraUnits = (product.extra_units || []).map(eu => ({
      id: eu.unit?.id || eu.id,
      name: eu.unit?.name || eu.name,
      symbol: eu.unit?.symbol || eu.symbol,
      conversion_factor: eu.conversion_factor
    }));
    if (product.hide_base_unit && normalizedExtraUnits.length > 0) {
      return normalizedExtraUnits[0];
    }
    return product.unit ? { ...product.unit, conversion_factor: 1 } : null;
  }, []);

  const getCartItemForProduct = useCallback((productId) => {
    return cartItems.find(item => item.product?.id === productId) || null;
  }, [cartItems]);

  const addProduct = useCallback(async (product) => {
    const unitObj = getUnitForProduct(product);
    if (!unitObj) return;
    try {
      setBusyProductId(product.id);
      const unitPrice = getEffectivePrice(product) * parseFloat(unitObj.conversion_factor || 1);
      await handleAddToCart(product, unitObj, 1, unitPrice);
    } catch (error) {
      console.error('[ProductSlider] Error adding to cart:', error);
    } finally {
      setBusyProductId(null);
    }
  }, [getUnitForProduct, getEffectivePrice, handleAddToCart]);

  const changeQuantity = useCallback(async (cartItem, change, productId) => {
    try {
      setBusyProductId(productId);
      const newQuantity = parseFloat(cartItem.quantity || 0) + change;
      if (newQuantity <= 0) {
        await handleRemoveFromCart(cartItem.id);
      } else {
        await handleUpdateItem(cartItem.id, newQuantity);
      }
    } catch (error) {
      console.error('[ProductSlider] Error changing quantity:', error);
    } finally {
      setBusyProductId(null);
    }
  }, [handleUpdateItem, handleRemoveFromCart]);

  const removeProduct = useCallback(async (cartItem, productId) => {
    try {
      setBusyProductId(productId);
      await handleRemoveFromCart(cartItem.id, productId);
    } catch (error) {
      console.error('[ProductSlider] Error removing from cart:', error);
    } finally {
      setBusyProductId(null);
    }
  }, [handleRemoveFromCart]);

  const handleLinkClick = (url, target = '_self') => {
    if (!url) return;
    const isInternal = url.startsWith('/') || (!url.startsWith('http://') && !url.startsWith('https://'));
    if (isInternal) {
      navigate(url);
    } else {
      window.open(url, target);
    }
  };

  if (!banner || products.length === 0) return null;

  return (
    <div
      className={`bg-white rounded-lg shadow-lg overflow-hidden ${className}`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {(banner.title || banner.link_url) && (
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-800 truncate">{banner.title}</h3>
            {banner.description && (
              <p className="text-xs text-gray-500 truncate">{banner.description}</p>
            )}
          </div>
          {banner.link_url && (
            <button
              type="button"
              onClick={() => handleLinkClick(banner.link_url, banner.link_target)}
              className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap ml-2"
            >
              View all
            </button>
          )}
        </div>
      )}

      <div className="relative px-2 py-2">
        <div className="overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${startIndex * (100 / perView)}%)` }}
          >
            {columns.map((column, columnIndex) => (
              <div
                key={`column-${columnIndex}`}
                className="flex-shrink-0 flex flex-col gap-2 self-start"
                style={{ width: `${100 / perView}%` }}
              >
                {column.map((product) => {
              const productId = product.id;
              const cartItem = getCartItemForProduct(productId);
              const quantity = cartItem ? parseFloat(cartItem.quantity || 0) : 0;
              const unitObj = getUnitForProduct(product);
              // Prices render per the card's display unit. Cart line prices
              // are already per the line's own unit; product and discount
              // prices are per BASE unit and must be multiplied by the
              // display unit's conversion factor — showing them raw put the
              // kg price next to a "/ pcs" label.
              const displayFactor = parseFloat(unitObj?.conversion_factor || 1) || 1;
              const cartUnitFactor = parseFloat(cartItem?.unit?.conversion_factor || 1) || 1;
              const hasAppliedDiscount = cartItem?.discount_percent > 0 && cartItem?.discounted_price;
              const displayedOriginalPrice = cartItem
                ? parseFloat(cartItem.price_at_add || 0) * (displayFactor / cartUnitFactor)
                : getEffectivePrice(product) * displayFactor;
              const displayedDiscountedPrice = hasAppliedDiscount
                ? parseFloat(cartItem.discounted_price) * (displayFactor / cartUnitFactor)
                : displayedOriginalPrice;
              const isBusy = busyProductId === productId;
              const discountBadgeInfo = isAuthenticated ? getDiscountBadgeInfo(productId) : null;
              const badgeText = discountBadgeInfo?.type === 'badge'
                ? discountBadgeInfo.text
                : discountBadgeInfo?.badgeConfig?.customText;
              const primaryImage =
                product.images?.find((image) => image.is_primary)?.image ||
                product.images?.[0]?.image ||
                FALLBACK_IMAGE;

              return (
                <div key={productId} className="px-1">
                  <div className="border rounded-md flex flex-col bg-white">
                    <div className="relative h-[130px] bg-gray-50 rounded-t-md">
                      <div className="absolute top-1 right-1 z-10 flex flex-col items-end gap-0.5">
                        {(product.badges || []).filter(b => b.is_active).slice(0, 2).map(badge => (
                          <span
                            key={badge.id}
                            className="text-[9px] px-1.5 py-0.5 rounded shadow-sm font-medium whitespace-nowrap"
                            style={{ backgroundColor: badge.color, color: badge.text_color }}
                            title={badge.name}
                          >
                            {badge.name}
                          </span>
                        ))}
                      </div>
                      {badgeText && (
                        <span
                          className="absolute top-1 left-1 z-10 text-[9px] px-1.5 py-0.5 rounded font-bold"
                          style={{
                            backgroundColor: discountBadgeInfo?.badgeConfig?.color || '#DC2626',
                            color: discountBadgeInfo?.badgeConfig?.textColor || '#FFFFFF'
                          }}
                        >
                          {badgeText}
                        </span>
                      )}
                      <img
                        src={imageError[productId] ? getImageUrl(FALLBACK_IMAGE) : getImageUrl(primaryImage)}
                        alt={product.name}
                        className="h-full w-full object-contain p-2"
                        onError={() => setImageError(prev => ({ ...prev, [productId]: true }))}
                      />
                      {quantity > 0 && (
                        <span className="absolute bottom-1 right-1 z-10 bg-yellow-500 text-black text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold shadow-lg border-2 border-white">
                          {quantity}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col flex-1 px-2 pb-2">
                      <h4
                        className="text-xs font-medium text-gray-800 line-clamp-2 min-h-[2.4em] mt-1"
                        title={product.name}
                      >
                        {product.name}
                      </h4>

                      {isAuthenticated && (
                        <div className="text-center my-1">
                          {hasAppliedDiscount ? (
                            <>
                              <span className="text-xs text-gray-400 line-through mr-1">
                                £{displayedOriginalPrice.toFixed(2)}
                              </span>
                              <span className="text-base font-bold text-green-600">
                                £{displayedDiscountedPrice.toFixed(2)}
                              </span>
                            </>
                          ) : discountBadgeInfo?.type === 'fixed_price' && discountBadgeInfo.originalPrice ? (
                            <>
                              <span className="text-xs text-gray-400 line-through mr-1">
                                £{(discountBadgeInfo.originalPrice * displayFactor).toFixed(2)}
                              </span>
                              <span className="text-base font-bold text-green-600">
                                £{(discountBadgeInfo.newPrice * displayFactor).toFixed(2)}
                              </span>
                            </>
                          ) : (
                            <span className="text-base font-bold text-gray-900">
                              £{(getEffectivePrice(product) * displayFactor).toFixed(2)}
                            </span>
                          )}
                          <span className="text-[0.7rem] text-gray-600 ml-1">
                            / {unitObj?.symbol || product.unit?.symbol || 'unit'}
                          </span>
                        </div>
                      )}

                      {isAuthenticated && product.alcohol_blocked && (
                        <div className="mt-auto text-center text-[0.7rem] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-1">
                          Premises licence required
                        </div>
                      )}
                      {isAuthenticated && !product.alcohol_blocked && (
                        <div className="mt-auto">
                          {quantity > 0 ? (
                            <div className="flex items-center justify-between">
                              <button
                                type="button"
                                className="border border-red-600 text-red-600 hover:bg-red-50 flex items-center justify-center w-[15%] h-[30px] rounded"
                                onClick={() => removeProduct(cartItem, productId)}
                                disabled={isBusy}
                                aria-label="Remove from cart"
                              >
                                <FiTrash2 className="w-3 h-3" />
                              </button>
                              <button
                                type="button"
                                className="bg-red-600 text-white hover:bg-red-700 flex items-center justify-center w-[20%] h-[30px] rounded"
                                onClick={() => changeQuantity(cartItem, -1, productId)}
                                disabled={isBusy}
                                aria-label="Decrease quantity"
                              >
                                <FiMinus className="w-3 h-3" />
                              </button>
                              <button
                                type="button"
                                className="bg-green-600 text-white hover:bg-green-700 flex items-center justify-center w-[20%] h-[30px] rounded"
                                onClick={() => changeQuantity(cartItem, 1, productId)}
                                disabled={isBusy}
                                aria-label="Increase quantity"
                              >
                                <FiPlus className="w-3 h-3" />
                              </button>
                              <button
                                type="button"
                                className="bg-green-600 text-white hover:bg-green-700 flex items-center justify-center w-[20%] h-[30px] rounded text-[10px]"
                                onClick={() => changeQuantity(cartItem, 5, productId)}
                                disabled={isBusy}
                                aria-label="Add 5"
                              >
                                <FiPlus className="w-2.5 h-2.5" />5
                              </button>
                              <button
                                type="button"
                                className="bg-green-600 text-white hover:bg-green-700 flex items-center justify-center w-[20%] h-[30px] rounded text-[10px]"
                                onClick={() => changeQuantity(cartItem, 10, productId)}
                                disabled={isBusy}
                                aria-label="Add 10"
                              >
                                <FiPlus className="w-2.5 h-2.5" />10
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="bg-blue-600 hover:bg-blue-700 text-white w-full h-[30px] rounded text-xs"
                              onClick={() => addProduct(product)}
                              disabled={isBusy}
                            >
                              {isBusy ? 'Adding...' : 'Add to Cart'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
                })}
              </div>
            ))}
          </div>
        </div>

        {maxIndex > 0 && (
          <>
            <button
              type="button"
              onClick={prev}
              className="absolute left-0 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 rounded-full p-1 z-20 focus:outline-none"
              aria-label="Previous products"
            >
              <FaChevronLeft className="w-3 h-3 text-white" />
            </button>
            <button
              type="button"
              onClick={next}
              className="absolute right-0 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 rounded-full p-1 z-20 focus:outline-none"
              aria-label="Next products"
            >
              <FaChevronRight className="w-3 h-3 text-white" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductSlider;
